import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getUser } from "../../../utils/storage";
import { axiosInstance } from "../../../utils/axios";
import { DocumentArrowUpIcon, TrashIcon } from "@heroicons/react/20/solid";
import PaginationV2 from "../../../components/PaginationV2";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import EditableCell from "../../../components/EditableCell";
import { toast } from "react-toastify";
import ColumnSearch from "../../../components/ColumnSearch";
import { XMarkIcon } from "@heroicons/react/24/outline";
import AbaNinjaTonerModal from "./AbaNinjaTonerModal";
import excelIcon from "../../../assets/excel-icon.webp";
import useDialogStore from "../../../stores/useDialogStore";
import ColumnSelector from "../../../components/ColumnSelector";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import ActionButtons from "../../../components/ActionButtons";
import NoData from "../../../components/NoData";
import LoadingComponent from "../../../components/LoadingComponent";
import Button from "../../../components/Button";
import EditableSelectCell from "../../../components/EditableSelectCell";

dayjs.extend(utc);
dayjs.extend(timezone);


export default function TonerStockList() {
  const { openDialog, closeDialog } = useDialogStore();
  const user = getUser();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "desc",
  });

  const [selectedColumns, setSelectedColumns] = useState({
    part_number: true,
    device_brand_id: true,
    machine_model: true,
    toner_model: true,
    color: true,
    units: true,
    price: true,
    weight: true,
    created_at: true,
  });

  const columnOptions = [
    { key: "part_number", label: "ID / Part No" },
    { key: "device_brand_id", label: "Brand" },
    { key: "machine_model", label: "Machine Model" },
    { key: "toner_model", label: "Toner Model" },
    { key: "color", label: "Color" },
    { key: "units", label: "Units" },
    { key: "price", label: "Price" },
    { key: "weight", label: "Weight" },
    { key: "created_at", label: "Date Registered" },
  ];

  // Fetch toners using React Query
  const {
    data: tonersData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["toners", location.search],
    queryFn: async () => {
      const params = new URLSearchParams(location.search);
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/toners?${params.toString()}`
      );
      return response.data;
    },
    refetchOnWindowFocus: false,
    staleTime: 0,
    cacheTime: 0,
  });

  const brands = useQuery({
    queryKey: ["brands"],
    queryFn: () =>
      axiosInstance.get(`${window.REACT_APP_API_ENDPOINT}/device-brands`),
    select: (data) => data.data.data.map(({ id, name }) => ({ id, name })),
  }).data;

  // Delete toner mutation
  const deleteTonerMutation = useMutation({
    mutationFn: (id) =>
      axiosInstance.delete(`${window.REACT_APP_API_ENDPOINT}/toners/${id}`),
    onSuccess: () => {
      refetch();
      toast.success("Toner deleted successfully!");
    },
    onError: (error) => {
      console.error("Delete toner error:", error);
      toast.error("Failed to delete toner.");
    },
  });

  // Edit toner mutation
  const editCellMutation = useMutation({
    mutationFn: ({ tonerId, field, value }) =>
      axiosInstance.put(
        `${window.REACT_APP_API_ENDPOINT}/toners/${tonerId}`,
        { [field]: value }
      ),
    onSuccess: () => {
      refetch();
      toast.success("Toner updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update toner. Please try again.");
      console.error("Update toner error:", error);
    },
  });

  const handleColumnSearch = (columnKey, query) => {
    const params = new URLSearchParams(location.search);
    if (query) {
      params.set(columnKey, query);
    } else {
      params.delete(columnKey);
    }
    params.delete("page");
    navigate(`${location.pathname}?${params.toString()}`);
    queryClient.invalidateQueries(["toners"]);
  };

  const handleColumnSort = (column, direction) => {
    const params = new URLSearchParams(location.search);
    const currentSortBy = params.get("sortBy");
    const currentOrder = params.get("order");

    if (currentSortBy === column) {
      const newDirection = currentOrder === "asc" ? "desc" : "asc";
      params.set("order", newDirection);
    } else {
      params.set("sortBy", column);
      params.set("order", "desc");
    }

    navigate(`${location.pathname}?${params.toString()}`);
    queryClient.invalidateQueries(["toners"]);
  };

  const handleClearFilters = () => {
    navigate(location.pathname);
    queryClient.invalidateQueries(["toners"]);
  };

  const handleDelete = (id) => {
    openDialog({
      title: "Delete Toner",
      content: (
        <div className="flex flex-col gap-4">
          <p className="text-lg">
            Are you sure you want to delete this toner?
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="light" size="sm" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              icon={<TrashIcon className="h-4 w-4" />}
              onClick={() => {
                deleteTonerMutation.mutate(id);
                closeDialog();
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ),
    });
  };

  const ColumnSelectorContent = ({ onExport }) => {
    const [localSelectedColumns, setLocalSelectedColumns] =
      useState(selectedColumns);

    return (
      <div className="bg-white rounded-lg w-full p-4">
        <ColumnSelector
          columns={columnOptions}
          selectedColumns={localSelectedColumns}
          setSelectedColumns={setLocalSelectedColumns}
        />
        <div className="mt-6 flex justify-end space-x-2">
          <Button variant="light" size="sm" onClick={() => closeDialog()}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setSelectedColumns(localSelectedColumns);
              onExport(localSelectedColumns);
            }}
          >
            {exportMutation?.isPending ? (
              <div className="flex items-center gap-2">
                <div className="loader h-4 w-4 border-[2px] border-t-transparent border-white rounded-full animate-spin"></div>
                Loading...
              </div>
            ) : (
              "Export"
            )}
          </Button>
        </div>
      </div>
    );
  };

  const handleOpenColumnSelector = () => {
    openDialog({
      title: "Select Columns to Export",
      content: (
        <ColumnSelectorContent
          onExport={(finalColumns) => {
            handleExport(finalColumns);
          }}
        />
      ),
    });
  };

  const exportMutation = useMutation({
    mutationFn: async (selectedColumnss) => {
      const params = new URLSearchParams(location.search);
      const response = await axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/export-excel/toners`,
        {
          columns: Object.entries(selectedColumnss)
            .filter(([_, selected]) => selected)
            .map(([key]) => key),
          params: params.toString(),
        },
        {
          responseType: "blob",
        }
      );
      return response;
    },
    onSuccess: (response) => {
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `toner_stock_export_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      closeDialog();
      toast.success("Report exported successfully!");
    },
    onError: (error) => {
      console.error("Export error:", error);
      toast.error("Failed to export report. Please try again.");
    },
  });

  const handleExport = async (selectedColumnss) => {
    if (Object.values(selectedColumnss).some((selected) => selected)) {
      exportMutation.mutate(selectedColumnss);
    } else {
      toast.warning("Please select at least one column to export");
    }
  };

  const toners = tonersData?.data || [];
  const paginationData = {
    current_page: tonersData?.pagination?.current_page || 1,
    last_page: tonersData?.pagination?.last_page || 1,
    next_page_url: tonersData?.pagination?.next_page_url,
    prev_page_url: tonersData?.pagination?.prev_page_url,
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 bg-bgBlue">
      <div className="sm:flex flex-col sm:items-start">
        <div className="w-full flex justify-between py-4 px-4 bg-white rounded-lg">
          <h3 className="font-bold text-2xl">👋 Hello {user.name}</h3>
        </div>
      </div>
      <div className="mt-8 flow-root bg-white rounded-lg">
        <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block lg:min-w-full max-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="w-full flex flex-col lg:flex-row justify-between items-center py-3 px-4 bg-white rounded-lg">
              <h4 className="font-semibold text-2xl mb-2 lg:mb-0">
                Toner Stock List
              </h4>
              <div className="flex flex-col gap-2 lg:flex-row items-center">
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<XMarkIcon className="h-4 w-4" />}
                  onClick={handleClearFilters}
                  className="mb-2 lg:mb-0"
                >
                  Clear Filters
                </Button>
                <Button
                  variant="outline-success"
                  size="sm"
                  icon={excelIcon}
                  onClick={handleOpenColumnSelector}
                  className="mb-2 lg:mb-0"
                >
                  Export Toner Stock Data as CSV
                </Button>
                <a
                  href="/toners/form"
                  className="border border-bluePrimary text-bluePrimary rounded-md px-3 py-2 text-sm font-bold mb-2 lg:mb-0"
                >
                  Register Toner +
                </a>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="lg:min-w-full divide-y divide-gray-300">
                <thead className="bg-black">
                  <tr>
                    <th className="px-4 py-4 text-left text-white text-sm">
                      <div className="flex items-center gap-2">
                        <ColumnSearch
                          columnName="ID / Part No"
                          columnKey="part_number"
                          onSearch={handleColumnSearch}
                          onSort={handleColumnSort}
                          sortConfig={sortConfig}
                          placeholder="Search by Part No..."
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm">
                      <div className="flex items-center gap-2">
                        <ColumnSearch
                          columnName="Brand"
                          columnKey="device_brand_id"
                          onSearch={handleColumnSearch}
                          onSort={handleColumnSort}
                          sortConfig={sortConfig}
                          placeholder="Search by Brand..."
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm">
                      <div className="flex items-center gap-2">
                        <ColumnSearch
                          columnName="Machine Model"
                          columnKey="machine_model"
                          onSearch={handleColumnSearch}
                          onSort={handleColumnSort}
                          sortConfig={sortConfig}
                          placeholder="Search by Machine..."
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm">
                      <div className="flex items-center gap-2">
                        <ColumnSearch
                          columnName="Toner Model"
                          columnKey="toner_model"
                          onSearch={handleColumnSearch}
                          onSort={handleColumnSort}
                          sortConfig={sortConfig}
                          placeholder="Search by Toner..."
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm">
                      <div className="flex items-center gap-2">
                        <ColumnSearch
                          columnName="Color"
                          columnKey="color"
                          onSearch={handleColumnSearch}
                          onSort={handleColumnSort}
                          sortConfig={sortConfig}
                          placeholder="Search by Color..."
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm">
                      <div className="flex items-center gap-2">
                        <ColumnSearch
                          columnName="Units"
                          columnKey="units"
                          onSearch={handleColumnSearch}
                          onSort={handleColumnSort}
                          sortConfig={sortConfig}
                          placeholder="Search by Units..."
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm">
                      <div className="flex items-center gap-2">
                        <ColumnSearch
                          columnName="Price"
                          columnKey="price"
                          onSearch={handleColumnSearch}
                          onSort={handleColumnSort}
                          sortConfig={sortConfig}
                          placeholder="Search by Price..."
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm">
                      <div className="flex items-center gap-2">
                        <ColumnSearch
                          columnName="Weight"
                          columnKey="weight"
                          onSearch={handleColumnSearch}
                          onSort={handleColumnSort}
                          sortConfig={sortConfig}
                          placeholder="Search by Weight..."
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm">
                      <div className="flex items-center gap-2">
                        <ColumnSearch
                          columnName="Date Registered"
                          columnKey="created_at"
                          onSearch={handleColumnSearch}
                          onSort={handleColumnSort}
                          sortConfig={sortConfig}
                          isDate={true}
                          placeholder="Search by Date..."
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {isLoading ? (
                    <tr>
                      <td colSpan={10} className="text-center">
                        <LoadingComponent />
                      </td>
                    </tr>
                  ) : toners.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center">
                        <NoData message="No toners found" />
                      </td>
                    </tr>
                  ) : (
                    toners?.map((toner, index) => (
                      <tr
                        key={toner.id}
                        className={`divide-y divide-gray-200 ${
                          index % 2 === 0 ? "bg-white" : "bg-gray-100"
                        }`}
                      >
                        <td className="whitespace-nowrap p-3 text-sm text-black">
                          <EditableCell
                            value={toner.part_number || "—"}
                            displayValue={toner.part_number || "—"}
                            id={toner.id}
                            field="part_number"
                            onSave={(data) =>
                              editCellMutation.mutate({
                                tonerId: data.id,
                                field: "part_number",
                                value: data.value,
                              })
                            }
                          />
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                          <div className="flex items-center pl-4">
                            {brands && (
                              <EditableSelectCell
                                value={toner.device_brand_id}
                                displayValue={toner.brand?.name}
                                id={toner.id}
                                options={brands}
                                field="device_brand_id"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    tonerId: data.id,
                                    field: "device_brand_id",
                                    value: data.value,
                                  })
                                }
                              />
                            )}
                          </div>
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm text-black">
                          <EditableCell
                            value={toner.machine_model || "—"}
                            displayValue={toner.machine_model || "—"}
                            id={toner.id}
                            field="machine_model"
                            onSave={(data) =>
                              editCellMutation.mutate({
                                tonerId: data.id,
                                field: "machine_model",
                                value: data.value,
                              })
                            }
                          />
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm text-black">
                          <EditableCell
                            value={toner.toner_model || "—"}
                            displayValue={toner.toner_model || "—"}
                            id={toner.id}
                            field="toner_model"
                            onSave={(data) =>
                              editCellMutation.mutate({
                                tonerId: data.id,
                                field: "toner_model",
                                value: data.value,
                              })
                            }
                          />
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm text-black">
                          <EditableCell
                            value={toner.color || "—"}
                            displayValue={toner.color || "—"}
                            id={toner.id}
                            field="color"
                            onSave={(data) =>
                              editCellMutation.mutate({
                                tonerId: data.id,
                                field: "color",
                                value: data.value,
                              })
                            }
                          />
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm text-black">
                          <EditableCell
                            value={toner.units}
                            displayValue={toner.units}
                            id={toner.id}
                            field="units"
                            onSave={(data) =>
                              editCellMutation.mutate({
                                tonerId: data.id,
                                field: "units",
                                value: parseInt(data.value),
                              })
                            }
                          />
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm text-black">
                          <EditableCell
                            value={toner.price}
                            displayValue={toner.price}
                            id={toner.id}
                            field="price"
                            onSave={(data) =>
                              editCellMutation.mutate({
                                tonerId: data.id,
                                field: "price",
                                value: parseFloat(data.value),
                              })
                            }
                          />
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm text-black">
                          <EditableCell
                            value={toner.weight}
                            displayValue={toner.weight}
                            id={toner.id}
                            field="weight"
                            onSave={(data) =>
                              editCellMutation.mutate({
                                tonerId: data.id,
                                field: "weight",
                                value: parseFloat(data.value),
                              })
                            }
                          />
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm text-black">
                          {dayjs.utc(toner.created_at).format("DD-MM-YYYY")}
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm sm:pl-0 flex items-center">
                          <ActionButtons
                            id={toner.id}
                            onDelete={() => handleDelete(toner.id)}
                          />
                          <Button
                            variant="outline-info"
                            size="xs"
                            icon={<DocumentArrowUpIcon className="h-4 w-4" />}
                            onClick={() => {
                              openDialog({
                                title: "Send Toner to AbaNinja",
                                content: (
                                  <AbaNinjaTonerModal
                                    selectedToners={[toner]}
                                    onClose={() => {
                                      closeDialog();
                                    }}
                                  />
                                ),
                              });
                            }}
                            title="Send to AbaNinja"
                            className="ml-2 px-2 py-1"
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <PaginationV2
              current_page={paginationData.current_page}
              last_page={paginationData.last_page}
              next_page_url={paginationData.next_page_url}
              prev_page_url={paginationData.prev_page_url}
            />
          </div>
        </div>
      </div>
    </div>
  );
}